import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Accordion, AccordionSummary,
  AccordionDetails, Alert, Stack, Divider, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { BookOpen, CheckCircle2 } from 'lucide-react';

interface UserGuideDialogProps {
  open: boolean;
  onClose: () => void;
}

const GUIDE_MARKDOWN = `# Sentinel Onboarding User Guide

Follow these 5 steps to onboard your project to Sentinel.

---

## Before You Start (Prerequisites)

- **LeanIX APP ID** — format: APP-XXXXX (e.g. APP-12345)
- **GHE repository URLs** + a Personal Access Token (PAT) with read access
- *(Optional)* SonarQube / BlackDuck / Harbor credentials for extended scanning

---

## Step 1 · Project Info

1. Enter your **project name** and **LeanIX APP ID** (APP-XXXXX).
2. Pick the **project phase** (e.g. Active Development, KTLO, Maintenance).
3. Set **criticality** (Low / Medium / High / Critical) and **data classification**.

---

## Step 2 · Repositories

1. Add one or more **GHE repository URLs** (HTTPS format).
2. Select a **target branch** for each repo.
   - Branch list is auto-fetched once your GHE PAT is validated in Step 3.
3. Tag each repo with **Criticality** (High / Medium / Low) and **Type** (Infra / Non-Infra).
4. Optionally set the **tech stack** (languages, frameworks, build tools).

---

## Step 3 · Connections

- **GHE PAT** — paste your Personal Access Token → click **Validate**.
- **SonarQube** — enter URL + token → **Validate & Save** → select your project in **Discover**.
- **BlackDuck** — enter URL + API token → **Validate & Save** → select your project in **Discover**.
- **Trivy / Harbor** — enter registry URL + username + secret → **Validate**.

> All connections are optional except GHE PAT (required for branch fetching and scanning).

---

## Step 4 · Membership

1. Add **team member emails** — they receive project access.
2. Set **management access**: SPOC, Mgmt-1, Mgmt-2, or Mgmt-3.
3. SPOCs, managers, and the onboarding submitter are auto-assigned the **Admin** role.

---

## Step 5 · Review & Submit

1. Review the **generated YAML** configuration.
2. Download **CI / CI-CD / PR pipeline** templates if needed.
3. Click **Submit**.

---

*Generated by Sentinel — https://sentinel.mercedes-benz.com*
`;

const STEPS = [
  {
    label: 'Project Info',
    bullets: [
      'Enter your project name and LeanIX APP ID (APP-XXXXX).',
      'Pick the project phase (e.g. Active Development, KTLO, Maintenance).',
      'Set criticality (Low / Medium / High / Critical) and data classification.',
    ],
  },
  {
    label: 'Repositories',
    bullets: [
      'Add one or more GHE repository URLs (HTTPS format).',
      'Select a target branch for each repo (branch list auto-fetched once GHE PAT is validated in Step 3).',
      'Tag each repo with Criticality (High / Medium / Low) and Type (Infra / Non-Infra).',
      'Optionally set the tech stack — languages, frameworks, build tools.',
    ],
  },
  {
    label: 'Connections',
    bullets: [
      'GHE PAT — paste your Personal Access Token → click Validate.',
      'SonarQube — enter URL + token → Validate & Save → select your project in Discover.',
      'BlackDuck — enter URL + API token → Validate & Save → select your project in Discover.',
      'Trivy / Harbor — enter registry URL + username + secret → Validate.',
    ],
    note: 'All connections are optional except GHE PAT (required for branch fetching and scanning).',
  },
  {
    label: 'Membership',
    bullets: [
      'Add team member emails — they receive project access.',
      'Set management access: SPOC, Mgmt-1, Mgmt-2, or Mgmt-3.',
      'SPOCs, managers, and the onboarding submitter are auto-assigned the Admin role.',
    ],
  },
  {
    label: 'Review & Submit',
    bullets: [
      'Review the generated YAML configuration.',
      'Download CI / CI-CD / PR pipeline templates if needed.',
      'Click Submit.',
    ],
  },
];

const UserGuideDialog: React.FC<UserGuideDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<number | false>(false);

  const handleAccordion = (idx: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? idx : false);
  };

  const handleDownload = () => {
    const blob = new Blob([GUIDE_MARKDOWN], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sentinel-onboarding-guide.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) =>
              t.palette.mode === 'dark'
                ? 'rgba(245, 166, 35, 0.12)'
                : 'rgba(11, 107, 203, 0.08)',
            flexShrink: 0,
          }}
        >
          <BookOpen size={18} color={theme.palette.primary.main} />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          Onboarding User Guide
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Follow these 5 steps to onboard your project to Sentinel.
        </Typography>

        {/* Prerequisites */}
        <Alert
          severity="info"
          icon={<CheckCircle2 size={18} />}
          sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
            Before You Start — Prerequisites
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">
                <strong>LeanIX APP ID</strong> — format: APP-XXXXX (e.g. APP-12345)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>GHE repository URLs</strong> + a Personal Access Token (PAT) with read access
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <em>(Optional)</em> SonarQube / BlackDuck / Harbor credentials for extended scanning
              </Typography>
            </li>
          </Stack>
        </Alert>

        {/* Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {STEPS.map((step, idx) => (
            <Accordion
              key={idx}
              expanded={expanded === idx}
              onChange={handleAccordion(idx)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: expanded === idx ? 'primary.main' : 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                transition: 'border-color 0.2s',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
              >
                <Chip
                  label={`Step ${idx + 1}`}
                  size="small"
                  color={expanded === idx ? 'primary' : 'default'}
                  sx={{ fontSize: 11, height: 22, fontWeight: 600, minWidth: 56 }}
                />
                <Typography variant="subtitle2" fontWeight={600}>
                  {step.label}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2, px: 2.5 }}>
                <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2 }}>
                  {step.bullets.map((b, bi) => (
                    <li key={bi}>
                      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                        {b}
                      </Typography>
                    </li>
                  ))}
                </Stack>
                {step.note && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1.25, fontStyle: 'italic' }}
                  >
                    {step.note}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>

      <Divider sx={{ mt: 2 }} />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          size="small"
        >
          Download Guide (.md)
        </Button>
        <Button variant="contained" onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserGuideDialog;
